import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../../context/GameContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiRefreshCw, FiHome, FiStar, FiCheck, FiArrowRight } = FiIcons;

// ─── Room Definitions ────────────────────────────────────────────────────────
const ROOMS = [
  {
    id: 'bedroom',
    name: 'Jungle Bedroom',
    emoji: '🛏️',
    bg: 'from-amber-100 to-yellow-50',
    borderColor: 'border-amber-300',
    accentColor: 'bg-amber-500',
    textColor: 'text-amber-700',
    steps: [
      {
        id: 'make-bed',
        instruction: 'First, make the bed!',
        hint: 'Tuck in those sheets and fluff the pillows 🛏️',
        emoji: '🛏️',
        itemsToClean: [
          { id: 'pillow', label: 'Messy Pillow', emoji: '🛏️', x: 20, y: 25, dirty: true },
          { id: 'blanket', label: 'Tangled Blanket', emoji: '🧸', x: 55, y: 40, dirty: true },
        ],
        successMsg: 'Great job! The bed looks cozy! 🌟',
      },
      {
        id: 'pick-toys',
        instruction: 'Next, pick up the toys!',
        hint: 'Put all the toys back in the toy box 🧸',
        emoji: '🧸',
        itemsToClean: [
          { id: 'toy1', label: 'Toy Car', emoji: '🚗', x: 15, y: 65, dirty: true },
          { id: 'toy2', label: 'Teddy Bear', emoji: '🧸', x: 70, y: 70, dirty: true },
          { id: 'toy3', label: 'Ball', emoji: '⚽', x: 45, y: 75, dirty: true },
        ],
        successMsg: 'Awesome! All toys are put away! 🎉',
      },
      {
        id: 'close-drawers',
        instruction: 'Last, close the open drawers!',
        hint: 'Push those drawers shut nice and neat 🗄️',
        emoji: '🗄️',
        itemsToClean: [
          { id: 'drawer1', label: 'Open Drawer', emoji: '🗄️', x: 30, y: 50, dirty: true },
          { id: 'drawer2', label: 'Open Drawer', emoji: '🗄️', x: 60, y: 50, dirty: true },
        ],
        successMsg: 'Perfect! The room is tidy! ✨',
      },
    ],
  },
  {
    id: 'kitchen',
    name: 'Safari Kitchen',
    emoji: '🍳',
    bg: 'from-green-100 to-teal-50',
    borderColor: 'border-green-300',
    accentColor: 'bg-green-500',
    textColor: 'text-green-700',
    steps: [
      {
        id: 'clear-table',
        instruction: 'First, clear the table!',
        hint: 'Remove all the dishes from the table 🍽️',
        emoji: '🍽️',
        itemsToClean: [
          { id: 'plate1', label: 'Dirty Plate', emoji: '🍽️', x: 25, y: 40, dirty: true },
          { id: 'cup1', label: 'Juice Cup', emoji: '🥤', x: 60, y: 35, dirty: true },
          { id: 'fork', label: 'Fork', emoji: '🍴', x: 42, y: 55, dirty: true },
        ],
        successMsg: 'Wonderful! The table is clear! 🌟',
      },
      {
        id: 'wipe-counter',
        instruction: 'Next, wipe the counter!',
        hint: 'Clean up those crumbs and spills 🧽',
        emoji: '🧽',
        itemsToClean: [
          { id: 'crumbs', label: 'Crumbs', emoji: '🟤', x: 20, y: 60, dirty: true },
          { id: 'spill', label: 'Spill', emoji: '💧', x: 55, y: 55, dirty: true },
          { id: 'crumbs2', label: 'More Crumbs', emoji: '🟤', x: 75, y: 65, dirty: true },
        ],
        successMsg: 'Sparkling clean counter! ✨',
      },
      {
        id: 'put-away-food',
        instruction: 'Last, put food back in the fridge!',
        hint: 'Everything goes back where it belongs 🧊',
        emoji: '🧊',
        itemsToClean: [
          { id: 'apple', label: 'Apple', emoji: '🍎', x: 30, y: 45, dirty: true },
          { id: 'milk', label: 'Milk', emoji: '🥛', x: 65, y: 40, dirty: true },
        ],
        successMsg: 'Kitchen is spotless! Amazing! 🎉',
      },
    ],
  },
  {
    id: 'playroom',
    name: 'Adventure Playroom',
    emoji: '🎮',
    bg: 'from-purple-100 to-pink-50',
    borderColor: 'border-purple-300',
    accentColor: 'bg-purple-500',
    textColor: 'text-purple-700',
    steps: [
      {
        id: 'stack-books',
        instruction: 'First, stack the books neatly!',
        hint: 'Line up those books on the shelf 📚',
        emoji: '📚',
        itemsToClean: [
          { id: 'book1', label: 'Story Book', emoji: '📗', x: 20, y: 50, dirty: true },
          { id: 'book2', label: 'Puzzle Book', emoji: '📘', x: 40, y: 65, dirty: true },
          { id: 'book3', label: 'Activity Book', emoji: '📕', x: 65, y: 45, dirty: true },
        ],
        successMsg: 'Books are stacked perfectly! 📚',
      },
      {
        id: 'put-blocks',
        instruction: 'Next, put the blocks in the box!',
        hint: 'Collect all the blocks and put them away 🧱',
        emoji: '🧱',
        itemsToClean: [
          { id: 'block1', label: 'Red Block', emoji: '🟥', x: 15, y: 70, dirty: true },
          { id: 'block2', label: 'Blue Block', emoji: '🟦', x: 50, y: 75, dirty: true },
          { id: 'block3', label: 'Yellow Block', emoji: '🟨', x: 75, y: 60, dirty: true },
          { id: 'block4', label: 'Green Block', emoji: '🟩', x: 35, y: 55, dirty: true },
        ],
        successMsg: 'All blocks collected! Super! 🌟',
      },
      {
        id: 'turn-off-lights',
        instruction: 'Last, turn off the lights!',
        hint: 'Tap all the light switches to turn them off 💡',
        emoji: '💡',
        itemsToClean: [
          { id: 'light1', label: 'Light Switch', emoji: '💡', x: 20, y: 30, dirty: true },
          { id: 'light2', label: 'Lamp', emoji: '🔦', x: 70, y: 35, dirty: true },
        ],
        successMsg: 'Playroom is perfect! You did it! 🎉',
      },
    ],
  },
];

// ─── Draggable Item in the scene ─────────────────────────────────────────────
function SceneItem({ item, stepIndex, onClean, isNew }) {
  const [cleaned, setCleaned] = useState(false);

  useEffect(() => {
    setCleaned(false);
  }, [stepIndex]);

  const handleClick = () => {
    if (cleaned) return;
    setCleaned(true);
    setTimeout(() => onClean(item.id), 400);
  };

  return (
    <motion.button
      initial={isNew ? { scale: 0, rotate: -20 } : { scale: 1 }}
      animate={cleaned
        ? { scale: 0, rotate: 20, opacity: 0 }
        : { scale: [1, 1.08, 1], rotate: [0, -3, 3, 0] }
      }
      transition={cleaned
        ? { duration: 0.35 }
        : { duration: 2.5, repeat: Infinity, repeatType: 'loop', delay: Math.random() * 1.5 }
      }
      onClick={handleClick}
      disabled={cleaned}
      className="absolute flex flex-col items-center gap-0.5 cursor-pointer select-none"
      style={{ left: `${item.x}%`, top: `${item.y}%`, transform: 'translate(-50%, -50%)' }}
    >
      <span className="text-4xl drop-shadow-md">{item.emoji}</span>
      <span className="bg-white/80 backdrop-blur-sm text-xs font-comic text-gray-700 px-1.5 py-0.5 rounded-full shadow border border-gray-200 whitespace-nowrap">
        {item.label}
      </span>
      {!cleaned && (
        <motion.div
          animate={{ scale: [1, 1.3, 1] }}
          transition={{ duration: 1, repeat: Infinity }}
          className="absolute -top-2 -right-2 w-4 h-4 bg-red-400 rounded-full border-2 border-white shadow"
        />
      )}
    </motion.button>
  );
}

// ─── Step Progress Dots ───────────────────────────────────────────────────────
function StepDots({ total, current, accentColor }) {
  return (
    <div className="flex gap-2 justify-center">
      {Array.from({ length: total }, (_, i) => (
        <motion.div
          key={i}
          animate={{ scale: i === current ? 1.3 : 1 }}
          className={`w-3 h-3 rounded-full border-2 border-white shadow ${i < current ? 'bg-green-500' : i === current ? accentColor : 'bg-gray-200'}`}
        />
      ))}
    </div>
  );
}

// ─── Room Selector Card ───────────────────────────────────────────────────────
function RoomCard({ room, index, onSelect }) {
  return (
    <motion.button
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ scale: 1.05, y: -6 }}
      whileTap={{ scale: 0.96 }}
      onClick={() => onSelect(room)}
      className={`bg-gradient-to-br ${room.bg} rounded-3xl p-6 shadow-xl border-4 ${room.borderColor} text-center cursor-pointer`}
    >
      <div className="text-5xl mb-3">{room.emoji}</div>
      <h3 className={`font-kid text-xl ${room.textColor} mb-1`}>{room.name}</h3>
      <p className="font-comic text-sm text-gray-500">3 cleanup steps</p>
      <div className="flex justify-center gap-1 mt-3">
        {room.steps.map((_, i) => (
          <div key={i} className={`w-2 h-2 rounded-full ${room.accentColor}`} />
        ))}
      </div>
    </motion.button>
  );
}

// ─── Main Game ────────────────────────────────────────────────────────────────
export default function CleanupQuestGame() {
  const navigate = useNavigate();
  const { state, dispatch } = useGame();

  const [phase, setPhase] = useState('select'); // select | intro | playing | stepWin | roomWin | allDone
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [currentStepIdx, setCurrentStepIdx] = useState(0);
  const [cleanedItems, setCleanedItems] = useState(new Set());
  const [roomsCompleted, setRoomsCompleted] = useState([]);
  const [totalScore, setTotalScore] = useState(0);
  const [wrongClicks, setWrongClicks] = useState(0);
  const [isNewItems, setIsNewItems] = useState(false);

  const currentStep = selectedRoom?.steps[currentStepIdx];
  const totalItemsInStep = currentStep?.itemsToClean.length || 0;
  const cleanedCount = cleanedItems.size;
  const stepComplete = cleanedCount >= totalItemsInStep;

  const handleSelectRoom = (room) => {
    setSelectedRoom(room);
    setCurrentStepIdx(0);
    setCleanedItems(new Set());
    setPhase('intro');
  };

  const handleStartPlaying = () => {
    setIsNewItems(true);
    setPhase('playing');
    setTimeout(() => setIsNewItems(false), 100);
  };

  const handleCleanItem = (itemId) => {
    const newSet = new Set(cleanedItems);
    newSet.add(itemId);
    setCleanedItems(newSet);

    const pts = 10;
    setTotalScore(s => s + pts);
    dispatch({ type: 'ADD_SCORE', payload: pts });

    // Check if step complete
    if (newSet.size >= totalItemsInStep) {
      setTimeout(() => setPhase('stepWin'), 600);
    }
  };

  const handleNextStep = () => {
    const nextIdx = currentStepIdx + 1;
    if (nextIdx >= selectedRoom.steps.length) {
      // Room complete!
      const newCompleted = [...roomsCompleted, selectedRoom.id];
      setRoomsCompleted(newCompleted);
      dispatch({ type: 'GAME_COMPLETED', payload: { questId: 'cleanup-quest', bonusTokens: 15 } });
      setPhase('roomWin');
    } else {
      setCurrentStepIdx(nextIdx);
      setCleanedItems(new Set());
      setIsNewItems(true);
      setPhase('playing');
      setTimeout(() => setIsNewItems(false), 100);
    }
  };

  const handleChooseAnother = () => {
    setPhase('select');
    setSelectedRoom(null);
    setCurrentStepIdx(0);
    setCleanedItems(new Set());
  };

  const handleRestart = () => {
    setPhase('select');
    setSelectedRoom(null);
    setCurrentStepIdx(0);
    setCleanedItems(new Set());
    setRoomsCompleted([]);
    setTotalScore(0);
    setWrongClicks(0);
  };

  const allRoomsDone = roomsCompleted.length >= ROOMS.length;

  // ─── ROOM SELECT ────────────────────────────────────────────────────────────
  if (phase === 'select') {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="min-h-screen p-4 pb-24 bg-gradient-to-br from-green-50 via-yellow-50 to-pink-50">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ y: -40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            className="flex items-center justify-between mb-8 pt-4">
            <button onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 bg-white rounded-2xl px-4 py-2 shadow-lg border-2 border-safari-200 font-comic text-jungle-700 hover:bg-safari-50">
              <SafeIcon icon={FiArrowLeft} /> Back
            </button>
            <div className="text-center">
              <h1 className="text-3xl font-kid text-jungle-700">🧹 Cleanup Quest!</h1>
              <p className="font-comic text-jungle-600 text-sm">Follow the 3 steps to tidy up!</p>
            </div>
            <div className="bg-white rounded-2xl px-4 py-2 shadow border-2 border-yellow-200 flex items-center gap-2">
              <span>🪙</span>
              <span className="font-kid text-jungle-700">{state.tokens}</span>
            </div>
          </motion.div>

          {/* Progress banner */}
          {roomsCompleted.length > 0 && (
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              className="bg-green-100 border-4 border-green-300 rounded-3xl p-4 mb-6 text-center">
              <p className="font-kid text-green-700 text-lg">
                ✅ {roomsCompleted.length}/{ROOMS.length} rooms cleaned! Keep going!
              </p>
            </motion.div>
          )}

          {/* Intro card */}
          <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            className="bg-white rounded-3xl p-6 shadow-xl border-4 border-safari-200 mb-8 text-center">
            <div className="text-5xl mb-3">🦁</div>
            <h2 className="font-kid text-2xl text-jungle-700 mb-2">Help tidy up the Jungle!</h2>
            <p className="font-comic text-jungle-600">
              Each room has <strong>3 steps</strong> to follow in order. Tap each messy item to clean it!
            </p>
            <div className="flex justify-center gap-6 mt-4">
              <div className="text-center">
                <div className="text-2xl">1️⃣</div>
                <p className="font-comic text-xs text-gray-500">Read the step</p>
              </div>
              <div className="text-center">
                <div className="text-2xl">👆</div>
                <p className="font-comic text-xs text-gray-500">Tap to clean</p>
              </div>
              <div className="text-center">
                <div className="text-2xl">🎉</div>
                <p className="font-comic text-xs text-gray-500">Earn tokens!</p>
              </div>
            </div>
          </motion.div>

          {/* Room cards */}
          <h3 className="font-kid text-xl text-jungle-700 mb-4 text-center">Choose a room to clean:</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {ROOMS.map((room, i) => (
              <div key={room.id} className="relative">
                {roomsCompleted.includes(room.id) && (
                  <div className="absolute -top-3 -right-3 z-10 bg-green-500 text-white rounded-full w-10 h-10 flex items-center justify-center text-xl shadow-lg border-2 border-white">
                    ✓
                  </div>
                )}
                <RoomCard room={room} index={i} onSelect={handleSelectRoom} />
              </div>
            ))}
          </div>

          {allRoomsDone && (
            <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              className="mt-8 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-3xl p-6 text-center text-white shadow-2xl">
              <div className="text-5xl mb-3">🏆</div>
              <h2 className="font-kid text-2xl mb-2">All Rooms Cleaned!</h2>
              <p className="font-comic mb-4">You're the ultimate Cleanup Champion!</p>
              <button onClick={handleRestart}
                className="bg-white text-orange-600 font-kid px-6 py-3 rounded-2xl shadow-lg hover:bg-orange-50 transition-all">
                Play Again! 🔄
              </button>
            </motion.div>
          )}
        </div>
      </motion.div>
    );
  }

  // ─── INTRO ──────────────────────────────────────────────────────────────────
  if (phase === 'intro') {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
        className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-green-50 to-yellow-50">
        <div className={`bg-gradient-to-br ${selectedRoom.bg} rounded-3xl p-10 shadow-2xl border-4 ${selectedRoom.borderColor} text-center max-w-md w-full`}>
          <motion.div animate={{ rotate: [0, -10, 10, 0] }} transition={{ duration: 2, repeat: Infinity }}
            className="text-7xl mb-4">{selectedRoom.emoji}</motion.div>
          <h2 className={`font-kid text-3xl ${selectedRoom.textColor} mb-3`}>{selectedRoom.name}</h2>
          <p className="font-comic text-gray-600 mb-6">
            Follow <strong>3 steps</strong> in order to clean this room!
          </p>
          <div className="space-y-3 mb-8">
            {selectedRoom.steps.map((step, i) => (
              <div key={step.id} className="flex items-center gap-3 bg-white/60 rounded-2xl p-3 text-left">
                <div className={`w-8 h-8 ${selectedRoom.accentColor} text-white rounded-full flex items-center justify-center font-kid text-sm flex-shrink-0`}>
                  {i + 1}
                </div>
                <div>
                  <p className="font-kid text-gray-800 text-sm">{step.instruction}</p>
                  <p className="font-comic text-xs text-gray-500">{step.itemsToClean.length} items to clean</p>
                </div>
                <span className="text-xl ml-auto">{step.emoji}</span>
              </div>
            ))}
          </div>
          <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
            onClick={handleStartPlaying}
            className={`w-full ${selectedRoom.accentColor} text-white font-kid text-xl py-4 rounded-2xl shadow-xl`}>
            Let's Clean! 🧹
          </motion.button>
          <button onClick={() => setPhase('select')}
            className="mt-3 text-gray-500 font-comic text-sm hover:text-gray-700">
            ← Choose different room
          </button>
        </div>
      </motion.div>
    );
  }

  // ─── STEP WIN ───────────────────────────────────────────────────────────────
  if (phase === 'stepWin') {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-green-100 to-yellow-50">
        <motion.div initial={{ scale: 0.6, rotate: -5 }} animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 200 }}
          className="bg-white rounded-3xl p-10 shadow-2xl border-4 border-green-300 text-center max-w-sm">
          <motion.div animate={{ scale: [1, 1.3, 1], rotate: [0, 15, -15, 0] }}
            transition={{ duration: 1, repeat: 2 }}
            className="text-7xl mb-4">⭐</motion.div>
          <h2 className="font-kid text-3xl text-green-700 mb-3">Step Complete!</h2>
          <p className="font-comic text-gray-600 mb-2">{currentStep?.successMsg}</p>
          <div className="bg-green-50 rounded-2xl p-3 mb-6 border-2 border-green-200">
            <p className="font-kid text-green-600">
              Step {currentStepIdx + 1} of {selectedRoom.steps.length} done!
            </p>
          </div>
          {currentStepIdx + 1 < selectedRoom.steps.length && (
            <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-3 mb-6">
              <p className="font-comic text-sm text-amber-700">
                ➡️ Next: <strong>{selectedRoom.steps[currentStepIdx + 1].instruction}</strong>
              </p>
            </div>
          )}
          <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
            onClick={handleNextStep}
            className="w-full bg-green-500 text-white font-kid text-xl py-4 rounded-2xl shadow-xl flex items-center justify-center gap-2">
            {currentStepIdx + 1 >= selectedRoom.steps.length ? '🏆 Finish Room!' : '➡️ Next Step!'}
          </motion.button>
        </motion.div>
      </motion.div>
    );
  }

  // ─── ROOM WIN ───────────────────────────────────────────────────────────────
  if (phase === 'roomWin') {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-yellow-100 to-orange-50">
        <motion.div initial={{ scale: 0.7 }} animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 180 }}
          className={`bg-gradient-to-br ${selectedRoom.bg} rounded-3xl p-10 shadow-2xl border-4 ${selectedRoom.borderColor} text-center max-w-md w-full`}>
          <motion.div animate={{ y: [-10, 10, -10] }} transition={{ duration: 2, repeat: Infinity }}
            className="text-7xl mb-4">🏆</motion.div>
          <h2 className={`font-kid text-3xl ${selectedRoom.textColor} mb-2`}>
            {selectedRoom.name} Cleaned!
          </h2>
          <p className="font-comic text-gray-600 mb-6">
            You followed all 3 steps perfectly! The room is spotless!
          </p>
          {/* Step recap */}
          <div className="space-y-2 mb-6">
            {selectedRoom.steps.map((step, i) => (
              <div key={step.id} className="flex items-center gap-3 bg-white/70 rounded-xl p-3">
                <div className="w-7 h-7 bg-green-500 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">
                  <SafeIcon icon={FiCheck} />
                </div>
                <span className="font-comic text-sm text-gray-700">{step.instruction}</span>
                <span className="ml-auto text-lg">{step.emoji}</span>
              </div>
            ))}
          </div>
          <div className="bg-yellow-100 border-2 border-yellow-300 rounded-2xl p-4 mb-6">
            <p className="font-kid text-yellow-700">+15 tokens earned! 🪙</p>
            <p className="font-comic text-sm text-yellow-600">
              {ROOMS.length - roomsCompleted.length > 0
                ? `${ROOMS.length - roomsCompleted.length} more room(s) to clean!`
                : '🎉 All rooms done!'}
            </p>
          </div>
          <div className="space-y-3">
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
              onClick={handleChooseAnother}
              className={`w-full ${selectedRoom.accentColor} text-white font-kid text-lg py-3 rounded-2xl shadow-lg`}>
              Clean Another Room! 🧹
            </motion.button>
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/dashboard')}
              className="w-full bg-white border-2 border-gray-200 text-gray-700 font-kid text-lg py-3 rounded-2xl">
              <SafeIcon icon={FiHome} className="inline mr-2" />
              Back to Dashboard
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    );
  }

  // ─── PLAYING ────────────────────────────────────────────────────────────────
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className={`min-h-screen bg-gradient-to-br ${selectedRoom.bg} pb-8`}>
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b-4 border-white shadow-md">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => setPhase('select')}
            className="flex items-center gap-1 bg-white rounded-xl px-3 py-1.5 shadow border-2 border-gray-200 font-comic text-sm text-gray-700">
            <SafeIcon icon={FiArrowLeft} /> Exit
          </button>
          <div className="text-center">
            <p className="font-kid text-lg text-gray-800">{selectedRoom.name}</p>
            <StepDots total={selectedRoom.steps.length} current={currentStepIdx} accentColor={selectedRoom.accentColor} />
          </div>
          <div className="flex items-center gap-1 bg-yellow-100 border-2 border-yellow-200 px-3 py-1.5 rounded-xl">
            <span className="text-lg">🪙</span>
            <span className="font-kid text-yellow-700">{state.tokens}</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 pt-4">
        {/* Instruction Banner */}
        <AnimatePresence mode="wait">
          <motion.div key={currentStep.id}
            initial={{ x: 80, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: -80, opacity: 0 }}
            className={`bg-white rounded-3xl p-5 shadow-xl border-4 ${selectedRoom.borderColor} mb-4`}>
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 ${selectedRoom.accentColor} text-white rounded-2xl flex items-center justify-center text-3xl shadow-lg flex-shrink-0`}>
                {currentStepIdx + 1}
              </div>
              <div className="flex-1">
                <p className={`font-kid text-xl ${selectedRoom.textColor}`}>{currentStep.instruction}</p>
                <p className="font-comic text-sm text-gray-500 mt-1">{currentStep.hint}</p>
              </div>
              <div className="text-4xl">{currentStep.emoji}</div>
            </div>
            {/* Mini progress */}
            <div className="mt-4">
              <div className="flex justify-between font-comic text-xs text-gray-500 mb-1">
                <span>Items cleaned</span>
                <span>{cleanedCount}/{totalItemsInStep}</span>
              </div>
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
                <motion.div
                  animate={{ width: `${(cleanedCount / totalItemsInStep) * 100}%` }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                  className={`h-full ${selectedRoom.accentColor} rounded-full`}
                />
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Scene */}
        <motion.div
          className={`relative bg-gradient-to-br ${selectedRoom.bg} rounded-3xl border-4 ${selectedRoom.borderColor} shadow-2xl overflow-hidden`}
          style={{ height: '55vh', minHeight: 320 }}>
          {/* Background decoration */}
          <div className="absolute inset-0 opacity-10 flex items-center justify-center">
            <span className="text-[12rem]">{selectedRoom.emoji}</span>
          </div>

          {/* Room label */}
          <div className="absolute top-3 left-3 bg-white/80 backdrop-blur-sm rounded-xl px-3 py-1 shadow">
            <span className="font-kid text-sm text-gray-700">{selectedRoom.name}</span>
          </div>

          {/* Items */}
          <AnimatePresence>
            {currentStep.itemsToClean.map((item) => (
              <SceneItem
                key={`${currentStep.id}-${item.id}`}
                item={item}
                stepIndex={currentStepIdx}
                onClean={handleCleanItem}
                isNew={isNewItems}
              />
            ))}
          </AnimatePresence>

          {/* Completion overlay */}
          {stepComplete && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="absolute inset-0 bg-green-500/20 backdrop-blur-sm flex items-center justify-center">
              <motion.div animate={{ scale: [1, 1.2, 1], rotate: [0, 5, -5, 0] }}
                transition={{ duration: 1, repeat: Infinity }}
                className="text-8xl">✨</motion.div>
            </motion.div>
          )}

          {/* Tap instruction */}
          {!stepComplete && (
            <motion.div
              animate={{ y: [0, -6, 0] }} transition={{ duration: 1.5, repeat: Infinity }}
              className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur-sm rounded-full px-4 py-2 shadow-lg border-2 border-gray-200">
              <p className="font-comic text-sm text-gray-600">👆 Tap each messy item to clean it!</p>
            </motion.div>
          )}
        </motion.div>

        {/* Step sequence reminder */}
        <div className="mt-4 grid grid-cols-3 gap-3">
          {selectedRoom.steps.map((step, i) => (
            <div key={step.id}
              className={`rounded-2xl p-3 text-center border-2 transition-all ${i < currentStepIdx
                ? 'bg-green-100 border-green-300'
                : i === currentStepIdx
                  ? `bg-white border-2 ${selectedRoom.borderColor} shadow-lg`
                  : 'bg-white/50 border-gray-200 opacity-50'}`}>
              <div className="text-2xl mb-1">
                {i < currentStepIdx ? '✅' : i === currentStepIdx ? step.emoji : '🔒'}
              </div>
              <p className={`font-comic text-xs ${i === currentStepIdx ? selectedRoom.textColor : 'text-gray-500'}`}>
                Step {i + 1}
              </p>
              <p className="font-kid text-xs text-gray-700 leading-tight">
                {step.instruction.replace('First, ', '').replace('Next, ', '').replace('Last, ', '')}
              </p>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}